'use client';

import { use, useEffect, useState } from 'react';
import Link from 'next/link';
import { Container, Section } from '@/components/primitives';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { BookCover } from '@/components/book-cover';
import { BookCarousel } from '@/components/book-carousel';
import { RentalGateButton } from '@/components/rental-gate-button';
import { Icon } from '@/components/icons';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/lib/toast-context';
import { useRouter } from 'next/navigation';
import { clientGetBook, clientListBooks, clientFavorites, clientAddFavorite, clientRemoveFavorite } from '@/lib/client-api';
import type { Book } from '@/types/catalog';

function Meta({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div style={{ color: 'var(--ink)', fontSize: 18, fontFamily: 'var(--serif)', fontWeight: 500, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function DetailsTab({ book }: { book: Book }) {
  const rows: Array<[string, string | number]> = [
    ['Título', book.title],
    ['Autor', book.author],
    ['Editorial', 'Fondo de Cultura Económica'],
    ...(book.year != null ? ([['Año de publicación', book.year]] as Array<[string, string | number]>) : []),
    ...(book.pages != null ? ([['Páginas', book.pages]] as Array<[string, string | number]>) : []),
    ...(book.isbn ? ([['ISBN', book.isbn]] as Array<[string, string | number]>) : []),
    ['Idioma', 'Español (México)'],
    ...(book.subject ? ([['Materia', book.subject]] as Array<[string, string | number]>) : []),
    ['Préstamo', '30 días, gratuito'],
  ];
  return (
    <div style={{ maxWidth: 680 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td
                style={{
                  padding: '14px 0',
                  borderBottom: '1px solid var(--rule)',
                  color: 'var(--stone)',
                  width: 220,
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  verticalAlign: 'top',
                }}
              >
                {k}
              </td>
              <td style={{ padding: '14px 0', borderBottom: '1px solid var(--rule)', color: 'var(--ink)' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The FCE catalog description sometimes carries HTML; render it as clean text.
function stripHtml(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function Synopsis({ book }: { book: Book }) {
  const text = stripHtml(book.fullSummary || book.summary || '');
  const blocks = text.split(/\n\n+/).filter(Boolean);
  if (blocks.length === 0) {
    return <p style={{ color: 'var(--stone)', fontSize: 16 }}>La sinopsis de este título estará disponible próximamente.</p>;
  }
  return (
    <div>
      {blocks.map((p, i) =>
        p.startsWith('**') ? (
          <h3 key={i} className="serif" style={{ fontSize: 20, margin: '28px 0 10px', fontWeight: 600 }}>
            {p.replace(/\*\*/g, '')}
          </h3>
        ) : (
          <p
            key={i}
            style={{ margin: '0 0 16px', color: 'var(--ink-soft)', fontSize: 16, lineHeight: 1.75, maxWidth: 680, textWrap: 'pretty' }}
          >
            {p}
          </p>
        ),
      )}
    </div>
  );
}

export default function BookPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params);
  const [book, setBook] = useState<Book | null>(null);
  const [related, setRelated] = useState<Book[]>([]);
  const [missing, setMissing] = useState(false);
  const [tab, setTab] = useState<'sinopsis' | 'detalles'>('sinopsis');
  const { loggedIn } = useAuth();
  const toast = useToast();
  const router = useRouter();
  const [fav, setFav] = useState(false);
  const [favBusy, setFavBusy] = useState(false);

  useEffect(() => {
    if (!loggedIn || !book) { setFav(false); return; }
    let cancelled = false;
    clientFavorites().then((items) => {
      if (!cancelled) setFav(items.some((f) => f.bookId === book.id));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- book identity via id
  }, [loggedIn, book?.id]);

  const toggleFavorite = async () => {
    if (!book || favBusy) return;
    if (!loggedIn) { router.push('/iniciar-sesion'); return; }
    setFavBusy(true);
    const ok = fav ? await clientRemoveFavorite(book.id) : await clientAddFavorite(book.id);
    setFavBusy(false);
    if (ok) setFav(!fav);
    else toast.show('No pudimos actualizar tus favoritos.', 'error');
  };

  const shareBook = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    try {
      if (navigator.share) await navigator.share({ title: book?.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.show('Enlace copiado al portapapeles.', 'success');
      }
    } catch { /* user dismissed the share sheet */ }
  };

  useEffect(() => {
    let active = true;
    setBook(null);
    setMissing(false);
    (async () => {
      const b = await clientGetBook(id);
      if (!active) return;
      if (!b) {
        setMissing(true);
        return;
      }
      setBook(b);
      if (b.categorySlug) {
        const rel = await clientListBooks({ category: b.categorySlug, limit: 12 });
        if (active) setRelated(rel.items.filter((x) => x.id !== b.id));
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  if (missing) {
    return (
      <div className="page-fade">
        <Header current="book" />
        <Section pad="xl">
          <Container>
            <h1 className="serif" style={{ fontSize: 40, fontWeight: 600 }}>Título no encontrado</h1>
            <p style={{ marginTop: 12, color: 'var(--ink-soft)' }}>
              El libro que buscas no está disponible. <Link href="/colecciones" className="under">Ver las colecciones</Link>.
            </p>
          </Container>
        </Section>
        <Footer />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="page-fade">
        <Header current="book" />
        <Section pad="xl">
          <Container>
            <div style={{ color: 'var(--stone)' }}>Cargando…</div>
          </Container>
        </Section>
      </div>
    );
  }

  const isFav = fav;
  const collectionSlug = book.categorySlug;
  const collectionLabel = book.categoryName || book.subject || 'Catálogo';

  return (
    <div className="page-fade">
      <Header current="book" />

      <Section pad="lg">
        <Container>
          <nav aria-label="Migas" style={{ fontSize: 13, color: 'var(--stone)', marginBottom: 36 }}>
            <Link href="/" className="under" style={{ textDecoration: 'none', color: 'var(--stone)' }}>Inicio</Link>
            <span style={{ margin: '0 10px' }}>/</span>
            {collectionSlug ? (
              <Link href={`/coleccion/${collectionSlug}`} className="under" style={{ textDecoration: 'none', color: 'var(--stone)' }}>
                {collectionLabel}
              </Link>
            ) : (
              <span>{collectionLabel}</span>
            )}
            <span style={{ margin: '0 10px' }}>/</span>
            <span style={{ color: 'var(--ink)' }}>{book.title}</span>
          </nav>

          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 64, alignItems: 'start' }}>
            <div className="m-unstick m-center" style={{ position: 'sticky', top: 96, maxWidth: 320 }}>
              <BookCover book={book} w={320} />
              <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <RentalGateButton book={book} />
                <button className="btn btn-ghost" style={{ justifyContent: 'center' }} disabled={favBusy} onClick={toggleFavorite}>
                  {isFav ? (
                    <>
                      <Icon.heartF /> En favoritos
                    </>
                  ) : (
                    <>
                      <Icon.heart /> Guardar en favoritos
                    </>
                  )}
                </button>
                <button className="btn btn-ghost" style={{ justifyContent: 'center' }} onClick={shareBook}>
                  <Icon.share /> Compartir
                </button>
              </div>
              <div
                style={{
                  marginTop: 22,
                  padding: '14px 0',
                  borderTop: '1px solid var(--rule)',
                  borderBottom: '1px solid var(--rule)',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  color: 'var(--stone)',
                  letterSpacing: '0.08em',
                  lineHeight: 2,
                }}
              >
                {book.isbn && <div>ISBN · {book.isbn}</div>}
                <div>EDICIÓN · Digital, FCE</div>
                <div>FORMATO · EPUB</div>
                <div>IDIOMA · Español</div>
              </div>
            </div>

            <div>
              {(book.categoryName || book.subject) && (
                <div className="eyebrow" style={{ marginBottom: 14 }}>{book.categoryName || book.subject}</div>
              )}
              <h1
                className="serif"
                style={{ margin: 0, fontSize: 52, lineHeight: 1.05, letterSpacing: '-0.02em', fontWeight: 600, textWrap: 'balance' }}
              >
                {book.title}
              </h1>
              {book.subtitle && (
                <div style={{ marginTop: 10, fontSize: 22, color: 'var(--ink-soft)', fontFamily: 'var(--serif)' }}>{book.subtitle}</div>
              )}
              <div style={{ marginTop: 18, fontSize: 18, color: 'var(--ink-soft)' }}>
                <span>por </span>
                <Link
                  href={`/autor?name=${encodeURIComponent(book.author)}`}
                  className="under"
                  style={{ textDecoration: 'none', fontWeight: 500, color: 'var(--ink)' }}
                >
                  {book.author}
                </Link>
              </div>

              <div style={{ marginTop: 28, display: 'flex', gap: 32, flexWrap: 'wrap', color: 'var(--stone)', fontSize: 13 }}>
                {book.year != null && <Meta label="Año" value={book.year} />}
                {book.pages != null && <Meta label="Páginas" value={book.pages} />}
                <Meta label="Editorial" value="FCE" />
              </div>

              <div style={{ marginTop: 40 }}>
                <div className="tabbar" role="tablist">
                  {(
                    [
                      ['sinopsis', 'Sinopsis'],
                      ['detalles', 'Detalles'],
                    ] as const
                  ).map(([k, l]) => (
                    <button key={k} className="tab" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
                      {l}
                    </button>
                  ))}
                </div>

                <div style={{ padding: '32px 0', minHeight: 280 }}>
                  {tab === 'sinopsis' && <Synopsis book={book} />}
                  {tab === 'detalles' && <DetailsTab book={book} />}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {related.length > 0 && (
        <>
          <hr className="rule-strong" />
          <Section pad="lg">
            <Container>
              <BookCarousel title="También en el catálogo" eyebrow={collectionLabel} books={related} />
            </Container>
          </Section>
        </>
      )}

      <Footer />
    </div>
  );
}
